/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-11-29 18:26:02
 *
 *******************************************************/
// @flow

import React, { useEffect, useState } from 'react';
import { MobileCancelMajor } from '@shopify/polaris-icons';
import { Button } from '@shopify/polaris';
import { getShop } from 'src/helpers';
import bookIcon from 'assets/images/common/book-icon.png';
import rocketIcon from 'assets/images/common/rocket-icon.png';
import wavingHandIcon from 'assets/images/common/waving-hand-icon.png';
import newUpdateIcon from 'assets/images/common/new-update-icon.png';
import timerIcon from 'assets/images/common/timer-icon.png';

type Props = {
  name: String,
  status: string,
  title: string,
  icon: 'process' | 'newUpdate',
  image?: File,
  backgroundImage?: File,
  hasOutline?: Boolean,
  titleIcon?: 'book' | 'rocket' | null,
  action?: {
    id?: string,
    /** Content the action displays */
    content?: string,
    icon?: File,
    primary: Boolean,
    // Disabled button
    disabled: Boolean,
    /** Alow to component custom */
    custom?: Boolean,
    /** Callback when an action takes place */
    onAction?: Function,
    /** Callback when mouse enter */
    onMouseEnter?: Function,
    /** Callback when element is touched */
    onTouchStart?: Function
  },
  secondaryAction?: {
    id?: string,
    /** Content the action displays */
    content?: string,
    /** Icon content for the action displays */
    icon?: File,
    /** Disabled button */
    disabled: Boolean,
    /** Alow to component custom */
    custom?: Boolean,
    /** Callback when an action takes place */
    onAction?: Function,
    /** Callback when mouse enter */
    onMouseEnter?: Function,
    /** Callback when element is touched */
    onTouchStart?: Function
  },
  allowDismiss: Boolean
};

const titleIconObj = {
  book: bookIcon,
  rocket: rocketIcon,
  wavingHand: wavingHandIcon
};

const iconCustomObj = {
  process: timerIcon,
  newUpdate: newUpdateIcon
};

// DONE
const PFSBanner = (props: Props) => {
  const [active, setActive] = useState(true);
  const {
    status = 'info',
    icon,
    name,
    titleIcon,
    hasOutline = false,
    title,
    image,
    action,
    secondaryAction,
    backgroundImage,
    children,
    allowDismiss
  } = props;

  useEffect(() => {
    // Check show info banner
    let dismiss = false;
    try {
      dismiss = localStorage.getItem(`${getShop()}-dismiss${name}`);
    } catch (error) {}

    // If the banner is dismissed, hide it
    if (dismiss === 'true') {
      setActive(false);
    }
  }, [name]);

  // Handle dismiss info banner
  const onDismiss = () => {
    try {
      localStorage.setItem(`${getShop()}-dismiss${name}`, true);
    } catch (error) {}
    setActive(false);
  };

  const renderActionByStatus = isSecond => {
    if (isSecond) {
      return (
        <div onClick={secondaryAction.onAction} className="secondary-action">
          {secondaryAction.content}
        </div>
      );
    }
    return (
      <div className={`action ${action.primary ? 'primary-action' : ''}`}>
        <Button disabled={action.disabled} onClick={action.onAction}>
          {action.icon && <img src={action.icon} alt="action-icon-primary" />}
          {action.content}
        </Button>
      </div>
    );
  };

  // Banner was dismissed
  if (!active) return null;

  return (
    <div
      className={`${name} pfs-app-banner pfs-app-banner--${status} ${
        hasOutline ? `banner-${status}--hasOutline` : ''
      }`}>
      {/* Render icon notify by status if have */}
      {icon && (
        <div className="pfs-banner-icon">
          <img width={20} height={20} alt={icon} src={iconCustomObj[icon]} />
        </div>
      )}
      {allowDismiss && (
        <div className="pfs-banner-dismiss">
          <Button onClick={onDismiss} icon={MobileCancelMajor} plain />
        </div>
      )}
      <div className="pfs-banner-infos">
        {/* Render title banner */}
        {title && (
          <div className="title">
            {titleIcon && (
              <img width={20} height={20} alt={titleIcon} src={titleIconObj[titleIcon]} />
            )}
            {title}
          </div>
        )}
        {/* Render infos banner */}
        <div className="pfs-banner-detail">
          <div style={{ width: `${image ? 70 : 100}%` }} className="pfs-banner-content">
            {/* render child */}
            {children}
            <div className="pfs-banner-actions">
              {action && (action.custom ? action.custom : renderActionByStatus())}
              {secondaryAction &&
                (secondaryAction.custom ? secondaryAction.custom : renderActionByStatus(true))}
            </div>
          </div>
          {/* Render image banner if exist image */}
          {image && (
            <div className="image">
              <img src={image} alt="pfs-banner-img" />
            </div>
          )}
          {backgroundImage && (
            <div className="backgroundImage">
              <img src={backgroundImage} alt="pfs-banner-backgroundImage" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PFSBanner;
